import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, Shield, Headphones, Clock, Users, Fuel, Star, 
  ThumbsUp, ChevronRight, ArrowRight, Phone, Sparkles, 
  Calendar, MapPin, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import TeamMember from '../components/TeamMember';

// Use environment variable for API base URL (default to relative path)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const Home = () => {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [featuredTours, setFeaturedTours] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const quickServices = [
    { icon: Car, title: 'Book a Car', desc: 'Choose your ride', color: 'bg-orange-500', iconColor: 'text-white', link: '/cars', delay: 0.1 },
    { icon: MapPin, title: 'Plan a Tour', desc: 'Explore Rwanda', color: 'bg-green-600', iconColor: 'text-white', link: '/tours', delay: 0.2 },
    { icon: Phone, title: '24/7 Support', desc: 'Call us anytime', color: 'bg-blue-600', iconColor: 'text-white', link: '/contact', delay: 0.3 },
    { icon: Calendar, title: 'Get Quote', desc: 'Custom offer', color: 'bg-purple-600', iconColor: 'text-white', link: '/contact', delay: 0.4 },
  ];

  const stats = [
    { number: '489+', label: 'Happy Customers', icon: ThumbsUp },
    { number: '56+', label: 'Luxury Vehicles', icon: Car },
    { number: '8+', label: 'Tour Packages', icon: MapPin },
    { number: '24/7', label: 'Customer Support', icon: Headphones },
  ];

  const features = [
    { icon: Car, title: 'Premium Fleet', desc: 'Access to the most exclusive luxury vehicles' },
    { icon: Shield, title: 'Fully Insured', desc: 'Complete peace of mind' },
    { icon: Headphones, title: 'Concierge Support', desc: '24/7 dedicated assistance' },
    { icon: Clock, title: 'Flexible Terms', desc: 'Customizable rental periods' },
  ];

  useEffect(() => {
    fetchHeroSlides();
    fetchFeaturedData();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (heroSlides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [heroSlides.length]);

  const fetchHeroSlides = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/hero-slides`);
      setHeroSlides(response.data);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
    }
  };

  const fetchFeaturedData = async () => {
    try {
      const [carsRes, toursRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/cars/featured`),
        axios.get(`${API_BASE_URL}/api/tours/featured`)
      ]);
      setFeaturedCars(carsRes.data);
      setFeaturedTours(toursRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/team`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  // Improved price display for cars
  const getCarPriceDisplay = (car) => {
    // If there's a price range with min and max
    if (car.min_price && car.max_price && car.min_price !== car.max_price) {
      // Check if they are numeric values
      const isNumericMin = !isNaN(parseFloat(String(car.min_price).replace(/,/g, '')));
      const isNumericMax = !isNaN(parseFloat(String(car.max_price).replace(/,/g, '')));
      
      if (isNumericMin && isNumericMax) {
        const minNum = parseFloat(String(car.min_price).replace(/,/g, ''));
        const maxNum = parseFloat(String(car.max_price).replace(/,/g, ''));
        return {
          type: 'range',
          display: `${minNum.toLocaleString()} - ${maxNum.toLocaleString()} RWF`,
          isNumeric: true
        };
      } else {
        return {
          type: 'range',
          display: `${car.min_price} - ${car.max_price}`,
          isNumeric: false
        };
      }
    } 
    // If only base price exists
    else if (car.base_price) {
      const isNumeric = !isNaN(parseFloat(String(car.base_price).replace(/,/g, '')));
      if (isNumeric) {
        const num = parseFloat(String(car.base_price).replace(/,/g, ''));
        return {
          type: 'single',
          display: `${num.toLocaleString()} RWF`,
          isNumeric: true
        };
      } else {
        return {
          type: 'single',
          display: car.base_price,
          isNumeric: false
        };
      }
    }
    return {
      type: 'single',
      display: 'Price on Request',
      isNumeric: false
    };
  };

  // Improved price display for tours
  const getTourPriceDisplay = (tour) => {
    if (typeof tour.price === 'number') {
      return {
        display: `${tour.price.toLocaleString()} RWF`,
        isNumeric: true
      };
    } else if (typeof tour.price === 'string') {
      // Check if string contains numbers
      const numericMatch = tour.price.match(/\d+/g);
      if (numericMatch) {
        return {
          display: tour.price,
          isNumeric: false
        };
      }
      return {
        display: tour.price,
        isNumeric: false
      };
    }
    return {
      display: 'Price on Request',
      isNumeric: false
    };
  };

  return (
    <div className="overflow-hidden bg-dark-800">
      {/* Hero Carousel Section */}
      <section className="relative h-screen w-full overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-dark-900/90 via-dark-800/60 to-transparent z-10"></div>
            <img 
              src={slide.image_url}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-2xl">
                  <motion.div
                    key={index + '-badge'}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <span className="inline-flex items-center space-x-2 bg-orange-500/20 backdrop-blur-sm px-4 py-2 rounded-full text-orange-400 text-sm">
                      <Sparkles size={16} />
                      <span>Experience Luxury Like Never Before</span>
                    </span>
                  </motion.div>
                  
                  <motion.h1
                    key={index + '-title'}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-6 mb-4 leading-tight"
                  >
                    {slide.title}
                  </motion.h1>
                  
                  <motion.p
                    key={index + '-subtitle'}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed"
                  >
                    {slide.subtitle}
                  </motion.p>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="flex flex-wrap gap-4"
                  >
                    <Link
                      to={slide.button_link || '/cars'}
                      className="group inline-flex items-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-orange-600 transition duration-300"
                    >
                      <span>{slide.button_text || 'Explore Vehicles'}</span>
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {heroSlides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-dark-700/50 hover:bg-dark-600/70 backdrop-blur-sm transition flex items-center justify-center text-white"
            >
              <ChevronRight size={20} className="rotate-180" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-dark-700/50 hover:bg-dark-600/70 backdrop-blur-sm transition flex items-center justify-center text-white"
            >
              <ChevronRight size={20} />
            </button>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'w-8 bg-orange-500' : 'w-4 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Quick Service Section */}
      <section className="relative -mt-16 z-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: service.delay }}
                whileHover={{ y: -8 }}
                className="glass-card rounded-2xl p-4 md:p-6 text-center group cursor-pointer"
                onClick={() => window.location.href = service.link}
              >
                <div className={`w-12 h-12 md:w-16 md:h-16 ${service.color} rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition duration-300 shadow-lg`}>
                  <service.icon size={24} className={`md:w-7 md:h-7 ${service.iconColor}`} />
                </div>
                <h3 className="font-bold text-white text-sm md:text-base">{service.title}</h3>
                <p className="text-xs text-gray-400 mt-1 hidden md:block">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-dark-700 border-b border-dark-600 mt-8">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-500/10 text-orange-500 mb-3">
                  <stat.icon size={22} />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white">{stat.number}</div>
                <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wide">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              The HYPA Experience
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Discover why discerning travelers choose HYPA MOTORS for their luxury transportation needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-elegant transition duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center mb-4">
                  <feature.icon size={24} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-dark-700">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wide">Our Team</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Meet Our Team
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Passionate professionals dedicated to providing you with an exceptional luxury experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <TeamMember key={member.id} member={member} index={index} />
            ))}
          </div>
        </div>
      </section>  */}

      {/* Featured Vehicles Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wide">Our Fleet</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Featured Luxury Vehicles
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore our curated selection of premium luxury vehicles
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCars.map((car, index) => {
                const priceInfo = getCarPriceDisplay(car);
                return (
                  <motion.div
                    key={car.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -10 }}
                    className="glass-card rounded-xl overflow-hidden hover:shadow-elegant-lg transition duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={car.image_url} 
                        alt={car.name} 
                        className="w-full h-full object-cover hover:scale-105 transition duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                          Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-white text-lg mb-1">{car.name}</h3>
                      <p className="text-gray-400 text-sm mb-3">{car.brand}</p>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3 text-sm text-gray-400">
                          <span className="flex items-center space-x-1">
                            <Users size={14} />
                            <span>{car.seats} Seats</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Fuel size={14} />
                            <span>{car.fuel_type}</span>
                          </span>
                        </div>
                      </div>
                      
                      {/* Improved Price Display for Cars */}
                      <div className="mb-4 text-center pt-3 border-t border-dark-600">
                        {priceInfo.type === 'range' ? (
                          <>
                            <div className="text-xl font-bold text-orange-500">
                              {priceInfo.display}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">price range per day</p>
                          </>
                        ) : (
                          <>
                            <div className="text-xl font-bold text-orange-500">
                              {priceInfo.display}
                            </div>
                            {priceInfo.isNumeric && (
                              <p className="text-xs text-gray-500 mt-1">per day</p>
                            )}
                          </>
                        )}
                      </div>
                      
                      <Link 
                        to={`/cars/${car.id}`}
                        className="block w-full text-center bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition text-sm font-medium"
                      >
                        View Details
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="text-center mt-10">
            <Link 
              to="/cars" 
              className="inline-flex items-center space-x-2 text-orange-500 hover:text-orange-400 font-medium"
            >
              <span>View All Vehicles</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Tours Section */}
      <section className="py-20 bg-dark-700">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wide">Tour Packages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Popular Tour Packages
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Discover the beauty of Rwanda with our guided tour experiences
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredTours.map((tour, index) => {
                const priceInfo = getTourPriceDisplay(tour);
                return (
                  <motion.div
                    key={tour.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -10 }}
                    className="glass-card rounded-xl overflow-hidden hover:shadow-elegant-lg transition duration-300"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={tour.image_url} 
                        alt={tour.name} 
                        className="w-full h-full object-cover hover:scale-105 transition duration-500"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                          {tour.duration}
                        </span>
                      </div>
                      {tour.featured && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center">
                            <Star size={12} className="mr-1 fill-current" />
                            Featured
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{tour.name}</h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{tour.description}</p>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <Calendar size={14} />
                          <span>{tour.duration}</span>
                        </div>
                        {tour.included && (
                          <div className="flex items-center space-x-1 text-sm text-gray-400">
                            <CheckCircle size={14} className="text-green-500" />
                            <span>{Array.isArray(tour.included) ? tour.included.length : 0} Inclusions</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Improved Price Display for Tours */}
                      <div className="mb-4 text-center pt-3 border-t border-dark-600">
                        <div className="text-xl font-bold text-orange-500">
                          {priceInfo.display}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">per person</p>
                      </div>
                      
                      <Link 
                        to={`/tours/${tour.id}`}
                        className="flex items-center justify-center space-x-2 w-full bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition font-medium"
                      >
                        <span>Book This Tour</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="text-center mt-10">
            <Link 
              to="/tours" 
              className="inline-flex items-center space-x-2 text-orange-500 hover:text-orange-400 font-medium"
            >
              <span>View All Tours</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for Your Next Adventure?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Contact our concierge team to book your preferred vehicle or tour package in Rwanda
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-600 transition"
            >
              Contact Us
            </Link>
            <a 
              href="tel:+250789369789" 
              className="bg-transparent border border-orange-500/30 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-500/10 transition"
            >
              Call +250 789 369 789
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;